import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface TrialStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  status: 'expired' | 'expiring';
  daysRemaining?: number;
}

const TrialStatusModal: React.FC<TrialStatusModalProps> = ({ 
  isOpen, 
  onClose, 
  isAdmin, 
  status,
  daysRemaining 
}) => {
  const navigate = useNavigate();
  const isExpired = status === 'expired';

  const handleUpgrade = () => {
    navigate('/settings/plans');
    onClose();
  };

  const getTitle = () => {
    return isExpired ? 'Trial Period Expired' : 'Trial Expiring Soon';
  };

  const getAlertMessage = () => {
    if (isExpired) {
      return 'Your trial period has ended';
    }
    const dayText = daysRemaining === 1 ? 'day' : 'days';
    return `Your trial expires in ${daysRemaining} ${dayText}`;
  };

  const getMainMessage = () => {
    if (isExpired) {
      return 'Thank you for trying WaveScoop! Your free trial period has expired.';
    }
    return `Your WaveScoop trial is ending soon. Upgrade now to ensure uninterrupted access to all features.`;
  };

  const getDescriptionMessage = () => {
    if (isExpired) {
      return "To continue using the platform and access all your simulations, you'll need to upgrade to one of our paid plans.";
    }
    return "Don't lose access to your simulations and ideas. Upgrade today to continue creating content with confidence.";
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Alert severity={isExpired ? 'error' : 'warning'} sx={{ mb: 2 }}>
            {getAlertMessage()}
          </Alert>
          
          <Typography variant="body1" gutterBottom>
            {getMainMessage()}
          </Typography>
          
          <Paper sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              {getDescriptionMessage()}
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              Our plans include more simulations per month, additional user seats, and more advanced features to help you get even more insights!
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      
      <DialogActions>
        {isAdmin ? (
          <>
            <Button onClick={onClose} color="inherit">
              {isExpired ? 'Not Now' : 'Remind Me Later'}
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleUpgrade}
            >
              View Plans & Upgrade
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => window.location.href = '/dashboard'}
            >
              Return to Dashboard
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TrialStatusModal;